package com.ijoin.payment

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import kotlin.js.JsName
import kotlin.js.Promise

@JsModule("@stripe/stripe-js")
@JsNonModule
external object StripeJsModule {
    @JsName("loadStripe")
    fun loadStripe(publishableKey: String, options: dynamic = definedExternally): Promise<Stripe?>
}

external interface Stripe {
    fun elements(options: dynamic = definedExternally): StripeElements
    fun confirmPayment(options: dynamic = definedExternally): Promise<ConfirmPaymentResult>
}

external interface StripeElements {
    fun create(elementType: String, options: dynamic = definedExternally): PaymentElement
}

external interface PaymentElement {
    fun mount(target: dynamic)
    fun unmount()
}

external interface ConfirmPaymentResult {
    val error: StripeError?
    val paymentIntent: StripePaymentIntent?
}

external interface StripePaymentIntent {
    val status: String?
}

external interface StripeError {
    val message: String?
}

actual object StripeCheckoutOverlayImpl {
    private var stripe: Stripe? = null
    private var elements: StripeElements? = null
    private var paymentElement: PaymentElement? = null
    private var overlayElement: HTMLElement? = null
    private var modalElement: HTMLElement? = null
    private var errorElement: HTMLElement? = null
    private var submitButton: HTMLButtonElement? = null
    private var closeButton: HTMLButtonElement? = null

    actual fun present(
        publishableKey: String,
        clientSecret: String,
        locale: String,
        onSuccess: () -> Unit,
        onError: (String) -> Unit,
    ) {
        dispose()
        val copy = stripeOverlayCopy(locale)
        val style = stripeOverlayStyle()
        val body = document.body ?: return
        val overlay = document.createElement("div") as HTMLElement
        overlay.classList.add("stripe-overlay")
        overlay.style.apply {
            setProperty("position", "fixed")
            setProperty("inset", "0")
            setProperty("background", style.overlay)
            setProperty("backdropFilter", "blur(10px)")
            setProperty("padding", "24px")
            setProperty("display", "flex")
            setProperty("alignItems", "center")
            setProperty("justifyContent", "center")
            setProperty("zIndex", "2147483647")
            setProperty("pointerEvents", "auto")
        }

        val modal = document.createElement("div") as HTMLElement
        modal.classList.add("stripe-modal")
        modal.style.apply {
            setProperty("background", style.surface)
            setProperty("borderRadius", style.radius)
            setProperty("padding", "32px 30px 30px")
            setProperty("width", "min(420px, 94vw)")
            setProperty("maxWidth", "min(460px, calc(100vw - 48px))")
            setProperty("margin", "0 auto")
            setProperty("boxShadow", style.shadow)
            setProperty("position", "relative")
            setProperty("zIndex", "2147483647")
            setProperty("pointerEvents", "auto")
            setProperty("color", style.text)
            setProperty("border", "1px solid ${style.border}")
            setProperty("fontFamily", "var(--app-font-family, 'Inter', 'Segoe UI', system-ui, sans-serif)")
        }

        val title = document.createElement("h3") as HTMLElement
        title.innerText = copy.title
        title.style.apply {
            setProperty("margin", "0 0 10px")
            setProperty("fontSize", "22px")
            setProperty("fontFamily", "var(--app-font-family, 'Inter', 'Segoe UI', system-ui, sans-serif)")
            setProperty("color", style.text)
        }

        val description = document.createElement("p") as HTMLElement
        description.innerText = copy.description
        description.style.apply {
            setProperty("margin", "0 0 16px")
            setProperty("fontSize", "15px")
            setProperty("color", style.textMuted)
            setProperty("lineHeight", "1.5")
        }

        val paymentContainer = document.createElement("div") as HTMLElement
        paymentContainer.style.setProperty("minHeight", "120px")
        paymentContainer.style.setProperty("marginTop", "4px")

        val errorText = document.createElement("p") as HTMLElement
        errorText.style.apply {
            setProperty("color", style.error)
            setProperty("fontSize", "14px")
            setProperty("minHeight", "20px")
            setProperty("marginTop", "8px")
        }

        val button = document.createElement("button") as HTMLButtonElement
        button.innerText = copy.payButton
        button.style.apply {
            setProperty("marginTop", "20px")
            setProperty("width", "100%")
            setProperty("padding", "15px 18px")
            setProperty("borderRadius", style.radiusPill)
            setProperty("border", "none")
            setProperty("background", style.primary)
            setProperty("color", style.onPrimary)
            setProperty("fontSize", "16px")
            setProperty("fontWeight", "600")
            setProperty("cursor", "pointer")
            setProperty("boxShadow", "0 18px 40px ${style.primaryShadow}")
            setProperty("transition", "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease")
            setProperty("fontFamily", "var(--app-font-family, 'Inter', 'Segoe UI', system-ui, sans-serif)")
        }

        val close = document.createElement("button") as HTMLButtonElement
        close.innerText = "\u00D7"
        close.style.apply {
            setProperty("position", "absolute")
            setProperty("top", "12px")
            setProperty("right", "12px")
            setProperty("border", "none")
            setProperty("background", "transparent")
            setProperty("fontSize", "22px")
            setProperty("cursor", "pointer")
            setProperty("color", style.textMuted)
        }

        modal.appendChild(close)
        modal.appendChild(title)
        modal.appendChild(description)
        modal.appendChild(paymentContainer)
        modal.appendChild(errorText)
        modal.appendChild(button)
        overlay.appendChild(modal)
        body.appendChild(overlay)

        overlayElement = overlay
        modalElement = modal
        errorElement = errorText
        submitButton = button
        closeButton = close

        fun showError(message: String) {
            errorText.innerText = message
        }

        close.onclick = {
            dispose()
            onError(copy.cancelMessage)
        }

        val stripeOptions = js("{}")
        stripeOptions.locale = locale
        StripeJsModule.loadStripe(publishableKey, stripeOptions)
            .then { loadedStripe ->
                if (loadedStripe == null) {
                    showError(copy.initError)
                    return@then
                }
                stripe = loadedStripe
                val options = js("{}")
                options.clientSecret = clientSecret
                options.locale = locale
                val appearance = js("{}")
                val variables = js("{}")
                variables.colorText = style.text
                variables.colorTextSecondary = style.textMuted
                variables.colorBackground = style.surface
                variables.colorDanger = style.error
                variables.fontFamily = "var(--app-font-family, 'Inter', 'Segoe UI', system-ui, sans-serif)"
                appearance.variables = variables
                val rules = js("{}")
                val labelRule = js("{}")
                labelRule.color = style.text
                labelRule.fontWeight = "500"
                rules[".InputLabel"] = labelRule
                val inputRule = js("{}")
                inputRule.color = style.text
                inputRule.backgroundColor = style.surface
                inputRule.borderColor = style.border
                inputRule.boxShadow = "none"
                rules[".Input"] = inputRule
                val inputFocusRule = js("{}")
                inputFocusRule.borderColor = style.primary
                inputFocusRule.boxShadow = "none"
                rules[".Input:focus"] = inputFocusRule
                val blockRule = js("{}")
                blockRule.backgroundColor = style.surface
                blockRule.borderColor = style.border
                rules[".Block"] = blockRule
                appearance.rules = rules
                options.appearance = appearance
                val createdElements = loadedStripe.elements(options)
                elements = createdElements
                val createdPaymentElement = createdElements.create("payment")
                paymentElement = createdPaymentElement
                createdPaymentElement.mount(paymentContainer)

                button.onclick = {
                    if (!button.disabled) {
                        button.disabled = true
                        button.innerText = copy.payButtonLoading
                        errorText.innerText = ""
                        val confirmParams = js("{}")
                        confirmParams.return_url = window.location.href
                        val confirmOptions = js("{}")
                        confirmOptions.elements = createdElements
                        confirmOptions.confirmParams = confirmParams
                        confirmOptions.redirect = "if_required"
                        loadedStripe.confirmPayment(confirmOptions)
                            .then { result ->
                                button.disabled = false
                                button.innerText = copy.payButton
                                when {
                                    result.error != null -> {
                                        showError(result.error?.message ?: copy.paymentFailed)
                                        onError(result.error?.message ?: copy.paymentFailed)
                                    }
                                    result.paymentIntent?.status == "succeeded" -> {
                                        onSuccess()
                                        dispose()
                                    }
                                    else -> {
                                        val status = result.paymentIntent?.status ?: "unknown"
                                        val message = copy.unexpectedStatus(status)
                                        showError(message)
                                        onError(message)
                                    }
                                }
                            }
                            .catch { error ->
                                button.disabled = false
                                button.innerText = copy.payButton
                                val message = error?.message ?: copy.processingFailed
                                showError(message)
                                onError(message)
                            }
                    }
                }
            }
            .catch { error ->
                val message = error?.message ?: copy.initError
                showError(message)
                onError(message)
            }
    }

    actual fun dispose() {
        paymentElement?.unmount()
        paymentElement = null
        elements = null
        stripe = null
        submitButton?.onclick = null
        closeButton?.onclick = null
        overlayElement?.remove()
        overlayElement = null
        modalElement = null
        errorElement = null
        submitButton = null
        closeButton = null
    }
}
